import { useCallback, useEffect, useRef, useState } from "react";
import { Handshake, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, isPinRequired } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { fmtMoney, num } from "../lib/calculations";
import { fmtDateTime } from "./Sales";
import PinGate from "../components/PinGate";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const inputCls = "w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors bg-white";
const TXN_TYPES = ["allocation", "withdrawal", "contribution"];
const txnLabel = { allocation: "ttAllocation", withdrawal: "ttWithdrawal", contribution: "ttContribution" };

export default function Partners() {
  const { t, lang } = useLanguage();
  const { can } = useAuth();
  const manage = can("partners.manage");
  const [partners, setPartners] = useState(null);
  const [pinLocked, setPinLocked] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", ownership_percentage: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [txnOpen, setTxnOpen] = useState(false);
  const [txnForm, setTxnForm] = useState({ type: "allocation", amount: "", date: "", notes: "" });
  const [savingTxn, setSavingTxn] = useState(false);

  const selectedRef = useRef(null);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/partners");
      setPartners(data);
      setPinLocked(false);
      const cur = selectedRef.current;
      if (cur) {
        const fresh = await api.get(`/partners/${cur.partner_id}`);
        setSelected(fresh.data);
      }
    } catch (err) {
      if (isPinRequired(err)) {
        setPinLocked(true);
      } else {
        toast.error(t("somethingWrong"));
      }
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const submitAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/partners", { ...form, ownership_percentage: num(form.ownership_percentage) });
      toast.success(t("partnerCreated"));
      setAddOpen(false);
      setForm({ name: "", ownership_percentage: "", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err, t("somethingWrong")));
    } finally {
      setSaving(false);
    }
  };

  const openPartner = async (p) => {
    try {
      const { data } = await api.get(`/partners/${p.partner_id}`);
      setSelected(data);
    } catch {
      toast.error(t("somethingWrong"));
    }
  };

  const submitTxn = async (e) => {
    e.preventDefault();
    setSavingTxn(true);
    try {
      await api.post(`/partners/${selected.partner_id}/transactions`, {
        ...txnForm,
        amount: num(txnForm.amount),
        date: txnForm.date || null,
      });
      toast.success(t("txnCreated"));
      setTxnOpen(false);
      setTxnForm({ type: "allocation", amount: "", date: "", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err, t("somethingWrong")));
    } finally {
      setSavingTxn(false);
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="partners-page">
      {pinLocked ? (
        <PinGate testid="partners-pin-gate" onSuccess={() => { setPinLocked(false); load(); }} />
      ) : (
        <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{t("partnersTitle")}</h1>
        {manage && (
          <button onClick={() => setAddOpen(true)} data-testid="add-partner-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm transition-colors">
            <Plus className="w-4 h-4" /> {t("addPartner")}
          </button>
        )}
      </div>

      {!partners ? (
        <div className="flex items-center justify-center py-20"><div className="w-10 h-10 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" /></div>
      ) : partners.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-16 text-center" data-testid="partners-empty">
          <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <Handshake className="w-7 h-7 text-emerald-500" />
          </div>
          <p className="font-semibold text-slate-800">{t("noPartners")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="partners-grid">
          {partners.map((p) => (
            <button key={p.partner_id} onClick={() => openPartner(p)} data-testid={`partner-card-${p.partner_id}`}
              className={`bg-white rounded-xl border p-5 text-start shadow-sm hover:shadow-md transition-all ${selected?.partner_id === p.partner_id ? "border-emerald-300 ring-2 ring-emerald-100" : "border-slate-200"}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="font-bold text-slate-900">{p.name}</p>
                <span className="text-xs font-bold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 tabular">{p.ownership_percentage}%</span>
              </div>
              <p className="text-xs text-slate-400 mb-2">{p.notes || "—"}</p>
              <p className="text-sm">
                <span className="text-slate-500">{t("balanceLabel")}: </span>
                <span className={`font-bold tabular ${p.balance < 0 ? "text-red-600" : "text-emerald-600"}`}>{fmtMoney(p.balance)}</span>
              </p>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="partner-detail">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="font-bold text-slate-900">{selected.name} — {t("transactionsLabel")}</h3>
            {manage && (
              <button onClick={() => setTxnOpen(true)} data-testid="add-partner-txn-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm transition-colors">
                <Plus className="w-4 h-4" /> {t("addTransaction")}
              </button>
            )}
          </div>
          {(selected.transactions || []).length === 0 ? (
            <p className="text-sm text-slate-400">{t("noTxns")}</p>
          ) : (
            <ul className="space-y-3">
              {selected.transactions.map((txn) => (
                <li key={txn.txn_id} className="flex items-center justify-between gap-3 border-b border-slate-50 pb-3" data-testid={`txn-${txn.txn_id}`}>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{t(txnLabel[txn.type])}</p>
                    <p className="text-xs text-slate-400">{txn.date} · {txn.created_by_name}{txn.notes ? ` · ${txn.notes}` : ""}</p>
                  </div>
                  <p className={`text-sm font-bold tabular ${txn.type === "withdrawal" ? "text-red-600" : "text-emerald-600"}`}>
                    {txn.type === "withdrawal" ? "-" : "+"}{fmtMoney(txn.amount)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="partner-dialog">
          <DialogHeader>
            <DialogTitle>{t("addPartner")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitAdd} className="space-y-4" data-testid="partner-form">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">{t("name")} *</label>
              <input data-testid="partner-name-input" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">{t("ownershipLabel")} *</label>
              <input data-testid="partner-ownership-input" type="number" min="0" max="100" step="0.01" required value={form.ownership_percentage} onChange={(e) => setForm((f) => ({ ...f, ownership_percentage: e.target.value }))} className={`${inputCls} tabular`} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">{t("notesLabel")}</label>
              <input data-testid="partner-notes-input" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className={inputCls} />
            </div>
            <button data-testid="partner-save-btn" type="submit" disabled={saving || !form.name || form.ownership_percentage === ""}
              className="w-full py-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("addPartner")}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={txnOpen} onOpenChange={setTxnOpen}>
        <DialogContent data-testid="partner-txn-dialog">
          <DialogHeader>
            <DialogTitle>{t("addTransaction")} — {selected?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitTxn} className="space-y-4" data-testid="partner-txn-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">{t("txnType")}</label>
                <select data-testid="txn-type-select" value={txnForm.type} onChange={(e) => setTxnForm((f) => ({ ...f, type: e.target.value }))} className={inputCls}>
                  {TXN_TYPES.map((tt) => <option key={tt} value={tt}>{t(txnLabel[tt])}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">{t("amountLabel")} *</label>
                <input data-testid="txn-amount-input" type="number" min="0.01" step="0.01" required value={txnForm.amount} onChange={(e) => setTxnForm((f) => ({ ...f, amount: e.target.value }))} className={`${inputCls} tabular`} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">{t("dateLabel")}</label>
              <input data-testid="txn-date-input" type="date" value={txnForm.date} onChange={(e) => setTxnForm((f) => ({ ...f, date: e.target.value }))} className={`${inputCls} tabular`} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">{t("notesLabel")}</label>
              <input data-testid="txn-notes-input" value={txnForm.notes} onChange={(e) => setTxnForm((f) => ({ ...f, notes: e.target.value }))} className={inputCls} />
            </div>
            <button data-testid="txn-save-btn" type="submit" disabled={savingTxn || !txnForm.amount}
              className="w-full py-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {savingTxn && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("addTransaction")}
            </button>
          </form>
        </DialogContent>
      </Dialog>
        </>
      )}
    </div>
  );
}
